import React from 'react';
import PropTypes from 'prop-types';
import {
  Card, Select, Table, Typography,
} from 'antd';
import { useSaveBeerRule } from '../hooks';
import { ruleToForm } from './BeerRules';
import * as fmt from '../format';

const { Paragraph } = Typography;

/** Name part of a tank-beer product, e.g. "Konbini KO0326" -> "Konbini". */
const nameAlias = (name) => String(name || '').split(/\s+/).filter((t) => !/\d/.test(t)).join(' ');

/**
 * Cin7 tank beer the planner couldn't tie to a Core beer. Assigning one adds its gyle prefix (or
 * name) to that beer's tank codes, so future gyles of the same beer match too.
 */
const TankBeer = ({ plan, canEdit }) => {
  const saveRule = useSaveBeerRule();
  const beers = plan.beers.filter((b) => !b.isMixedPack);

  const assign = (gyle, beerName) => {
    const beer = beers.find((b) => b.beerName === beerName);
    const code = gyle.suggestedPrefix || nameAlias(gyle.name);
    const current = ruleToForm(beer);
    const codes = [...beer.rules.gyleCodes, code.toUpperCase()];
    saveRule.mutate({ beerName, ...current, gyleCodes: [...new Set(codes)].join(', ') });
  };

  return (
    <Card className="forecasting-section" title="Tank beer not matched to a Core beer">
      <Paragraph type="secondary">
        Most of these are NPD or one-off brews. If one belongs to a Core beer, assign it so its
        litres count as incoming stock (e.g. Konbini base can be given to one of the Konbini
        flavours).
      </Paragraph>
      <Table
        rowKey="sku"
        size="small"
        pagination={{ pageSize: 10, hideOnSinglePage: true }}
        dataSource={plan.unmatchedTankBeer}
        columns={[
          { title: 'Gyle', dataIndex: 'name' },
          { title: 'Location', dataIndex: 'locations' },
          {
            title: 'In tank', dataIndex: 'availableLitres', align: 'right', render: fmt.litres,
          },
          { title: 'Brewed', dataIndex: 'brewDate', render: fmt.shortDate },
          {
            title: 'Belongs to',
            width: 260,
            render: (_, gyle) => (
              <Select
                showSearch
                allowClear
                disabled={!canEdit}
                style={{ width: 240 }}
                placeholder="Assign to a Core beer"
                options={beers.map((b) => ({ value: b.beerName, label: b.beerName }))}
                onChange={(beerName) => beerName && assign(gyle, beerName)}
              />
            ),
          },
        ]}
      />
    </Card>
  );
};

TankBeer.propTypes = {
  plan: PropTypes.shape({
    beers: PropTypes.arrayOf(PropTypes.object),
    unmatchedTankBeer: PropTypes.arrayOf(PropTypes.object),
  }).isRequired,
  canEdit: PropTypes.bool.isRequired,
};

export default TankBeer;
